import { useState } from 'react';
// @mui
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material';
// components
import Iconify from '@/components/Iconify';
import { SectionLabel } from '../../..';
// redux
import { dispatch, useSelector } from '@/stores';
import {
  selectPaymentMethod,
  setValidationError,
} from '@/features/bookings-management/slices';
// utils
import {
  buildWalletLabel,
  setEvaIcon,
} from '@/features/bookings-management/utils';

export default function PaymentMethodSelect() {
  const booking = useSelector((state) => state.booking);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const wallets = booking.client?.wallet ?? [];
  const hasClient = Boolean(booking.client?.id);
  const hasWallets = wallets.length > 0;
  const paymentError = booking.validationErrors.payment_method_id;
  const selectedWallet = wallets.find(
    (wallet) => wallet.id === booking.paymentMethodId
  );

  return (
    <Stack spacing={1.25} sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <SectionLabel label="Payment Method:" />
        <Button
          size="small"
          startIcon={<Iconify icon={setEvaIcon(!selectedWallet)} />}
          disabled={!hasClient || !hasWallets}
          onClick={() => setIsDialogOpen(true)}
        >
          {selectedWallet ? 'Change' : 'Add'}
        </Button>
      </Stack>

      {!hasClient && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Select client first
        </Typography>
      )}

      {hasClient && !hasWallets && (
        <Alert severity="warning">
          This client has no saved card payment methods. Create the booking,
          then send a payment method request.
        </Alert>
      )}

      {hasClient && hasWallets && (
        <>
          <Typography
            variant={selectedWallet ? 'subtitle2' : 'body2'}
            sx={{ color: selectedWallet ? 'text.primary' : 'text.secondary' }}
          >
            {selectedWallet
              ? buildWalletLabel(selectedWallet)
              : 'Select saved card'}
          </Typography>
          {paymentError && <FormHelperText>{paymentError}</FormHelperText>}
        </>
      )}

      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <DialogTitle>Select payment method</DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Stack spacing={0.75}>
            {wallets.map((wallet) => (
              <ListItemButton
                key={wallet.id}
                selected={wallet.id === booking.paymentMethodId}
                onClick={() => {
                  dispatch(selectPaymentMethod(wallet.id));
                  dispatch(
                    setValidationError({
                      field: 'payment_method_id',
                      error: null,
                    })
                  );
                  setIsDialogOpen(false);
                }}
                sx={{
                  borderRadius: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="subtitle2">
                  {buildWalletLabel(wallet)}
                </Typography>
              </ListItemButton>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
